import { useState, useEffect, useCallback } from 'react';
import { db } from '@/config/firebase';
import { doc, getDoc } from 'firebase/firestore';

// Field names exactly as they appear in Firestore
export const FOUR_WEEK_PPC_FIELDS = [
  '4 Week Overall Trend: PPC Spend',
  '4 Week Overall Trend: PPC Sales',
  '4 Week Overall Trend: PPC CPC',
  '4 Week Overall Trend: PPC ACoS'
] as const;

export interface FourWeekPPCData {
  spend: number[];
  sales: number[];
  cpc: number[];
  acos: number[];
}

export interface ChartDataPoint {
  week: string;
  spend?: number;
  sales?: number;
  cpc?: number;
  acos?: number;
}

export interface ProcessedChartData {
  spendSalesData: ChartDataPoint[];
  cpcAcosData: ChartDataPoint[];
}

interface UseFourWeekPPCDataResult {
  data: ProcessedChartData | null;
  rawData: FourWeekPPCData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Parse pipe-separated string into number array
 * Example: "970|992|1230|1211" -> [970, 992, 1230, 1211]
 */
const parsePipeData = (value: string | null | undefined): number[] => {
  if (!value || typeof value !== 'string') {
    return [0, 0, 0, 0]; // Default 4 weeks of zeros
  }

  try {
    const parts = value.split('|');
    const numbers = parts.map(part => {
      const num = parseFloat(part.trim());
      return isNaN(num) ? 0 : num;
    });

    // Ensure we always have exactly 4 weeks
    while (numbers.length < 4) {
      numbers.push(0);
    }

    return numbers.slice(0, 4); // Take only first 4 values
  } catch (error) {
    console.warn('Error parsing pipe data:', value, error);
    return [0, 0, 0, 0];
  }
};

/**
 * Transform raw data into chart-ready format
 */
const processChartData = (rawData: FourWeekPPCData): ProcessedChartData => {
  const weeks = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];

  // Create data for Spend & Sales chart
  const spendSalesData: ChartDataPoint[] = weeks.map((week, index) => ({
    week,
    spend: rawData.spend[index] || 0,
    sales: rawData.sales[index] || 0,
  }));

  // Create data for CPC & ACoS chart
  const cpcAcosData: ChartDataPoint[] = weeks.map((week, index) => ({
    week,
    cpc: rawData.cpc[index] || 0,
    acos: rawData.acos[index] || 0,
  }));

  return {
    spendSalesData,
    cpcAcosData
  };
};

/**
 * Hook to fetch 4-week PPC data from pmt2_data collection
 * Fetches data from /pmt2_data/{companyName}/products/{asin}
 */
export const useFourWeekPPCData = (
  companyName?: string,
  asin?: string
): UseFourWeekPPCDataResult => {
  const [rawData, setRawData] = useState<FourWeekPPCData | null>(null);
  const [data, setData] = useState<ProcessedChartData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchFourWeekPPCData = useCallback(async () => {
    if (!companyName || !asin) {
      setRawData(null);
      setData(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch document from pmt2_data collection
      const docRef = doc(db, 'pmt2_data', companyName, 'products', asin);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const docData = docSnap.data();
        
        // Extract and parse the 4 pipe-separated fields
        const spend = parsePipeData(docData[FOUR_WEEK_PPC_FIELDS[0]]);
        const sales = parsePipeData(docData[FOUR_WEEK_PPC_FIELDS[1]]);
        const cpc = parsePipeData(docData[FOUR_WEEK_PPC_FIELDS[2]]);
        const acos = parsePipeData(docData[FOUR_WEEK_PPC_FIELDS[3]]);

        const rawPPCData: FourWeekPPCData = {
          spend,
          sales,
          cpc,
          acos
        };

        // Process data for charts
        const processedData = processChartData(rawPPCData);

        setRawData(rawPPCData);
        setData(processedData);
      } else {
        // Document doesn't exist, set all fields to zeros
        const emptyData: FourWeekPPCData = {
          spend: [0, 0, 0, 0],
          sales: [0, 0, 0, 0],
          cpc: [0, 0, 0, 0],
          acos: [0, 0, 0, 0]
        };

        const processedData = processChartData(emptyData);
        setRawData(emptyData);
        setData(processedData);
      }
    } catch (err) {
      console.error('Error fetching 4-week PPC data:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch 4-week PPC data'));
    } finally {
      setIsLoading(false);
    }
  }, [companyName, asin]);

  const refetch = useCallback(() => {
    fetchFourWeekPPCData();
  }, [fetchFourWeekPPCData]);

  useEffect(() => {
    fetchFourWeekPPCData();
  }, [fetchFourWeekPPCData]);

  return {
    data,
    rawData,
    isLoading,
    error,
    refetch
  };
};

export default useFourWeekPPCData;